import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from "./context/auth-context";
import { AuthProvider } from './context/auth-context';
import { SidebarProvider } from './context/sidebar-context';
import { ThemeProvider } from './context/theme-context';
import { useTranslation } from 'react-i18next';
import ToastContainer from './components/generics/toast-container';
import CrispChat from './components/ui/CrispChat';
import { getValidLangPath } from './utils/language';
import MobileRouteGuard from './components/auth/mobile-route-guard';

// Pages publiques
import TermsPage from './view/public/terms-page';
import PrivacyPage from './view/public/privacy-page';
import FaqPage from './view/public/faq-page';
import LoginPage from './view/auth/login-page';
import SignupPage from './view/auth/signup-page';
import EmailVerification from './view/auth/email-verification';
import ForgotPassword from './view/auth/forgot-password';
import ResetPasswordForm from './view/auth/reset-password-form';

// Pages client
import BookingClient from './view/client/booking-client';
import BookingManage from './view/client/booking-manage';
import ReviewSend from './view/client/review-send';
import PaymentSuccess from './view/client/payment-success';
import PaymentCancel from './view/client/payment-cancel';

// Pages professionnel
import ProviderLayout from './components/layout/ProviderLayout';
import ProviderDashboard from './view/professional/dashboard';
import ProviderCalendar from './view/professional/calendar';
import ProviderReservations from './view/professional/reservations';
import ProviderServices from './view/professional/services';
import ProviderAvailability from './view/professional/availability';
import ProviderProfile from './view/professional/profile';
import ProviderReviews from './view/professional/reviews';
import ProviderCompany from './view/professional/company';
import ProviderSubscription from './view/professional/subscription';
import ProviderBilling from './view/professional/billing';
import ProviderStripeConnect from './view/professional/stripe-connect';
import ProviderPromoCodes from './view/professional/promo-codes';


interface ProtectedRouteProps {
  element: React.ReactNode;
  requiredAccountType: 'client' | 'provider';
}

const ProtectedRoute = ({ element, requiredAccountType }: ProtectedRouteProps) => {
  const { accountType } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';

  if (accountType !== requiredAccountType) {
    return <Navigate to={`/${lang}/${accountType}/dashboard`} replace />;
  }

  return <>{element}</>;
};

const RedirectWithLang = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const newPath = getValidLangPath(location.pathname, i18n);
  return newPath ? <Navigate to={newPath} replace /> : null;
};

const RedirectToHome = () => {
  React.useEffect(() => {
    window.location.href = process.env.REACT_APP_HOME_URL || 'https://prestaslot.com';
  }, []);
  return null;
};

const publicRoutes = [
  { path: "terms", element: <TermsPage /> },
  { path: "privacy", element: <PrivacyPage /> },
  { path: "faq", element: <FaqPage /> },
  { path: "login", element: <MobileRouteGuard><LoginPage /></MobileRouteGuard> },
  { path: "signup", element: <MobileRouteGuard><SignupPage /></MobileRouteGuard> },
  { path: "verify-email", element: <EmailVerification /> },
  { path: "forgot-password", element: <ForgotPassword /> },
  { path: "reset-password", element: <ResetPasswordForm /> },
  { path: "review/send/:token", element: <ReviewSend /> },
];

const bookingRoutes = [
  { path: ":domain/:businessName", element: <BookingClient /> },
  { path: "booking-manage", element: <BookingManage /> },
  { path: "payment/success", element: <PaymentSuccess /> },
  { path: "payment/cancel", element: <PaymentCancel /> },
];

function App() {
  // Remplacer les placeholders SEO si la serverless function n'a pas été exécutée (dev local)
  React.useEffect(() => {
    if (document.title.includes('__SEO_')) {
      document.title = 'PrestaSlot - Gestion de planning et réservations professionnelles';
    }
    document.querySelectorAll('meta, link').forEach((el) => {
      ['content', 'href', 'hreflang'].forEach((attr) => {
        const val = el.getAttribute(attr);
        if (val?.includes('__SEO_')) {
          el.remove();
        }
      });
    });
    if (document.documentElement.lang.includes('__SEO_')) {
      document.documentElement.lang = 'fr';
    }
  }, []);

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <div className="App">
            <ToastContainer />
            <CrispChat websiteId="a981140f-1f3d-4fb2-aec5-2f46228f5765" />
            <Routes>
              <Route path="/" element={<RedirectToHome />} />
              <Route path="/:lang">
                {publicRoutes.map(({ path, element }) => (
                  <Route key={path} path={path} element={element} />
                ))}
                {bookingRoutes.map(({ path, element }) => (
                  <Route key={path} path={path} element={element} />
                ))}
                <Route path="provider" element={
                  <ProtectedRoute
                    element={
                      <SidebarProvider>
                        <ProviderLayout />
                      </SidebarProvider>
                    }
                    requiredAccountType="provider"
                  />
                }>
                  <Route path="dashboard" element={<ProviderDashboard />} />
                  <Route path="calendar" element={<ProviderCalendar />} />
                  <Route path="reservations" element={<ProviderReservations />} />
                  <Route path="services" element={<ProviderServices />} />
                  <Route path="availability" element={<ProviderAvailability />} />
                  <Route path="reviews" element={<ProviderReviews />} />
                  <Route path="profile" element={<ProviderProfile />} />
                  <Route path="companies" element={<ProviderCompany />} />
                  <Route path="promo-codes/:companyId" element={<ProviderPromoCodes />} />
                  <Route path="subscription" element={<ProviderSubscription />} />
                  <Route path="billing" element={<ProviderBilling />} />
                  <Route path="stripe-connect" element={<ProviderStripeConnect />} />
                </Route>
              </Route>

              {/* Redirections automatiques pour routes sans langue */}
              {[
                "/provider/*",
                "/:domain/:businessName",
                "/booking-manage",
                "/terms",
                "/privacy",
                "/faq",
                "/login",
                "/signup",
                "/verify-email",
                "/forgot-password",
                "/reset-password",
              ].map((path) => (
                <Route key={path} path={path} element={<RedirectWithLang />} />
              ))}

              <Route path="*" element={<RedirectWithLang />} />
            </Routes>
          </div>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
